<template>
    <div class="dispensacion-container">
        <h2 class="titulo">{{ isEditMode ? 'Editar Dispensación' : 'Crear Dispensación Medicinal' }}</h2>

        <form @submit.prevent="handleSubmit">
            <!-- Usuario -->
            <div class="mb-3">
                <label>Usuario Medicinal:</label>
                <input type="text" :value="usuarioNombre" disabled class="form-control" />
            </div>

            <!-- Producto -->
            <div class="mb-3">
                <label>Buscar y seleccionar producto:</label>
                <b-form-input v-model="searchQuery" placeholder="Buscar por tipo o código..." class="mb-3" />

                <div class="productos-grid">
                    <div v-for="op in paginatedProductos" :key="op.value" class="producto-card"
                        :class="{ seleccionado: selectedProducto === op.value }" @click="selectedProducto = op.value">
                        <div class="producto-header">
                            <strong>{{ op.full.tipo_producto }}</strong>
                            <span class="codigo">{{ op.full.codigo }}</span>
                        </div>
                        <div class="stock-info">
                            {{ op.full.stock_disponible }} u. de {{ op.full.peso_por_unidad }} {{
                                op.full.unidad_medida_peso_envase }}
                        </div>
                    </div>
                </div>

                <BPagination v-model="currentPage" :total-rows="filteredProductos.length" :per-page="perPage"
                    align="center" class="my-3" />
            </div>

            <!-- Cantidad -->
            <div class="mb-3">
                <label for="cantidad" class="form-label">
                    <font-awesome-icon :icon="['fas', 'calculator']" />
                    Cantidad a dispensar:
                </label>
                <div class="input-group">
                    <button 
                        type="button" 
                        class="btn btn-outline-secondary" 
                        @click="restarCantidad"
                        :disabled="cantidad <= 1"
                        title="Reducir cantidad"
                    >
                        <font-awesome-icon :icon="['fas', 'minus']" />
                    </button>
                    <input 
                        id="cantidad"
                        type="number" 
                        v-model="cantidad" 
                        min="1" 
                        step="1" 
                        required 
                        class="form-control text-center"
                        :max="stockDisponible"
                        @input="validarCantidad"
                    />
                    <button 
                        type="button" 
                        class="btn btn-outline-secondary" 
                        @click="sumarCantidad"
                        :disabled="cantidad >= stockDisponible"
                        title="Aumentar cantidad"
                    >
                        <font-awesome-icon :icon="['fas', 'plus']" />
                    </button>
                </div>
                <div class="form-text">
                    <font-awesome-icon :icon="['fas', 'info-circle']" />
                    Stock disponible: {{ stockDisponible }} unidades
                </div>
            </div>

            <!-- Anulado -->
            <div class="mb-3">
                <label>Anulado:</label>
                <select v-model="anulado" class="form-select">
                    <option :value="false">No</option>
                    <option :value="true">Sí</option>
                </select>
            </div>

            <!-- Justificación -->
            <div v-if="anulado" class="mb-3">
                <div class="alert alert-warning mb-3">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
                    <strong>Importante:</strong> Anular una dispensación reintegra automáticamente la cantidad de la dispensacion al stock del producto.
                </div>
                <label for="justificacion" class="form-label">
                    <font-awesome-icon :icon="['fas', 'comment']" />
                    Justificación de Anulación:
                </label>
                <textarea 
                    id="justificacion"
                    v-model="justificacionAnulacion" 
                    required 
                    class="form-control" 
                    rows="3"
                    placeholder="Por favor, explique el motivo de la anulación..."
                />
            </div>

            <!-- Botones -->
            <div class="d-flex justify-content-between mt-4">
                <button type="submit" class="btn btn-primary">
                    {{ isEditMode ? 'Actualizar' : 'Asignar' }} Dispensación
                </button>
                <button type="button" class="btn btn-outline-secondary" @click="$emit('cancelar')">Cancelar</button>
            </div>
        </form>
    </div>
</template>

<script>
import axios from '@/axios'
import { BFormInput, BPagination } from 'bootstrap-vue-next'

export default {
    props: {
        dispensacion: { type: Object, default: null },
        usuario: { type: Object, required: true }
    },
    data() {
        return {
            productos: [],
            searchQuery: '',
            selectedUsuario: this.usuario.usuario_medicinal_id,
            usuarioNombre: `${this.usuario.nombre} ${this.usuario.apellido}`,
            selectedProducto: this.dispensacion?.producto_final_envasado_id || null,
            cantidad: this.dispensacion?.cantidad_entregada || 1,
            anulado: this.dispensacion?.anulado || false,
            justificacionAnulacion: this.dispensacion?.justificacion_anulacion || '',
            identificacion: this.usuario.identificacion,
            currentPage: 1,
            perPage: 6
        }
    },
    computed: {
        isEditMode() {
            return !!this.dispensacion
        },
        filteredProductos() {
            const q = this.searchQuery.toLowerCase()
            return this.productos
                .filter(p => p.tipo_producto?.toLowerCase().includes(q) || p.codigo?.toLowerCase().includes(q))
                .map(p => ({
                    value: p.id,
                    text: `${p.tipo_producto} - ${p.codigo}`,
                    full: p
                }))
        },
        paginatedProductos() {
            const start = (this.currentPage - 1) * this.perPage
            return this.filteredProductos.slice(start, start + this.perPage)
        },
        stockDisponible() {
            const seleccionado = this.productos.find(p => p.id === this.selectedProducto)
            return seleccionado ? seleccionado.stock_disponible : 0
        }
    },
    methods: {
        async fetchProductos() {
            try {
                const club_id = this.$store.getters.getClubActual.id
                const { data } = await axios.get(`/api/producto_final_envasado_con_stock?club_id=${club_id}`)
                this.productos = data.map(p => ({
                    ...p,
                    cantidad_actual: p.stock_disponible * p.peso_por_unidad,
                }))
            } catch (err) {
                console.error('Error al obtener productos envasados:', err)
            }
        },
        async handleSubmit() {
            if (!Number.isInteger(this.cantidad) || this.cantidad < 1) {
                return alert('La cantidad debe ser un número entero positivo.')
            }
            const seleccionado = this.productos.find(p => p.id === this.selectedProducto)
            if (seleccionado && this.cantidad > seleccionado.stock_disponible) {
                return alert(`La cantidad excede el stock disponible (${seleccionado.stock_disponible})`)
            }
            if (this.anulado && !this.justificacionAnulacion) {
                return alert('Debe justificar la anulación.')
            }

            const club_id = this.$store.getters.getClubActual.id
            const data = {
                usuario_medicinal_id: this.selectedUsuario,
                producto_final_envasado_id: this.selectedProducto,
                cantidad_entregada: this.cantidad,
                club_id,
                anulado: this.anulado,
                justificacion_anulacion: this.justificacionAnulacion,
                identificacion: this.identificacion
            }

            try {
                if (this.isEditMode) {
                    await axios.put(`/api/dispensacion-medicinal/${this.dispensacion.id}`, data)
                    alert('Dispensación actualizada exitosamente')
                    this.$emit('actualizar')
                } else {
                    await axios.post('/api/dispensacion-medicinal', data)
                    alert('Dispensación asignada exitosamente')
                    this.$emit('guardar')
                }
            } catch (err) {
                console.error('Error al guardar:', err)
                alert('Error al procesar la dispensación')
            }
        },
        restarCantidad() {
            if (this.cantidad > 1) {
                this.cantidad--
            }
        },
        sumarCantidad() {
            const seleccionado = this.productos.find(p => p.id === this.selectedProducto)
            if (seleccionado && this.cantidad < seleccionado.stock_disponible) {
                this.cantidad++
            }
        },
        validarCantidad() {
            const seleccionado = this.productos.find(p => p.id === this.selectedProducto)
            if (seleccionado && this.cantidad > seleccionado.stock_disponible) {
                this.cantidad = seleccionado.stock_disponible
            } else if (this.cantidad < 1) {
                this.cantidad = 1
            }
        }
    },
    mounted() {
        this.fetchProductos()
    },
    components: {
        BFormInput,
        BPagination
    }
}
</script>

<style scoped>
.dispensacion-container {
    max-width: 820px;
    margin: 0 auto;
    background: #fff;
    padding: 2rem 2.5rem;
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}

.titulo {
    font-size: 1.8rem;
    color: #205375;
    font-weight: 600;
    margin-bottom: 1.5rem;
}

.productos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1rem;
}

.producto-card {
    border: 1px solid #ccc;
    border-radius: 8px;
    padding: 1rem;
    cursor: pointer;
    background: #f9f9f9;
    transition: all 0.3s ease;
}

.producto-card:hover {
    background: #e9f5ff;
    border-color: #007bff;
}

.producto-card.seleccionado {
    background: #e0f0ff;
    border: 2px solid #007bff;
    box-shadow: 0 0 12px rgba(0, 123, 255, 0.4);
}

.producto-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}

.codigo {
    font-size: 0.9rem;
    color: #666;
}

.stock-info {
    font-size: 0.85rem;
    color: #444;
}
</style>